import { useEffect, useRef, useState } from "react";
import { callFunction } from "../api/callFunction";
import EventSuggestionAPI, { ClaimConflictError } from "../api/EventSuggestionApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { buildSuggestionConversionState } from "./utils";
import SimilarEventsPanel from "./SimilarEventsPanel";
import {
  CalendarIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  HandRaisedIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  PlayCircleIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon } from "@heroicons/react/20/solid";
import {
  getConfidenceBadge,
  formatMiles,
  getDistanceMiles,
  getRelativeDate,
  getRelativeDateColor,
} from "./eventSuggestionsHelpers";
import { MiniMap } from "./MiniMap";
import { SuggestionCardActions } from "./SuggestionCardActions";
import { variantURL, fallbackToOriginal } from "../utils/imageVariant";

// Claims go through EventSuggestionAPI.claim, a transaction that only takes the
// idea if its pickedUpBy still matches what this card rendered.

export const SuggestionCard = ({ suggestion, onUpdateStatus, isSuperAdmin, vendorID, hosts, usersById = {}, origin = null, mapMode = false, showMap = !mapMode }) => {
  const [cardOpen, setCardOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showReasoning, setShowReasoning] = useState(false);
  const [pickingUp, setPickingUp] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  const [hasHovered, setHasHovered] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef(null);

  const hasVideo = !videoFailed
    && typeof suggestion.videoUrl === "string" && suggestion.videoUrl.length > 0;

  // Play on hover, and keep looping the whole time the card is expanded — an open
  // card shouldn't need the pointer parked on it (and touch devices never hover).
  const shouldPlay = hasVideo && (hovering || cardOpen);

  // The <video> mounts lazily (first hover / open), so play/pause is driven here
  // rather than in the handlers — at mount time the ref is still null when the
  // handler runs. videoPlaying (the crossfade) resets here too, so a hover-out on
  // an open card doesn't fade the still back in over a clip that's still going.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (shouldPlay) {
      const playPromise = el.play();
      if (playPromise) playPromise.catch(() => {});
    } else {
      el.pause();
      el.currentTime = 0;
      setVideoPlaying(false);
    }
  }, [shouldPlay]);

  const handleImageEnter = () => {
    if (!hasVideo) return;
    setHasHovered(true);
    setHovering(true);
  };

  const handleImageLeave = () => {
    setHovering(false);
  };

  // "Who liked this" — super-admin-only. The likes subcollection is owner-only
  // readable (firestore.rules), so we fetch resolved liker identities through the
  // getSuggestionLikers callable on demand (first expand), then cache them.
  const [showLikers, setShowLikers] = useState(false);
  const [likers, setLikers] = useState(null);
  const [likersLoading, setLikersLoading] = useState(false);

  const toggleLikers = async () => {
    const next = !showLikers;
    setShowLikers(next);
    if (next && likers === null && !likersLoading) {
      setLikersLoading(true);
      try {
        const res = await callFunction("getSuggestionLikers", { suggestionId: suggestion.id });
        setLikers(res?.likers || []);
      } catch (err) {
        console.error("[EventSuggestions] getSuggestionLikers error:", err);
        toast.error("Failed to load who liked this");
        setShowLikers(false);
      } finally {
        setLikersLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!showMoreMenu) return;
    const handler = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMoreMenu]);
  const navigate = useNavigate();

  const isPickedUpByMe = suggestion.pickedUpBy === vendorID;
  const isPickedUpByOther = suggestion.pickedUpBy && !isPickedUpByMe;
  const pickedUpByHost = isPickedUpByOther
    ? (usersById[suggestion.pickedUpBy] || hosts.find((h) => h.id === suggestion.pickedUpBy))
    : null;
  const pickedUpByName = pickedUpByHost
    ? (pickedUpByHost.name || pickedUpByHost.email || pickedUpByHost.phone || `host …${String(pickedUpByHost.id).slice(-4)}`)
    : "another host";

  const handleReturnToPool = async () => {
    try {
      await EventSuggestionAPI.returnToPool(suggestion.id);
      toast.success("Returned to pool");
    } catch (err) {
      console.error("[EventSuggestions] Return to pool error:", err);
      toast.error("Failed to return to pool");
    }
  };

  const handleAssign = async (targetVendorID) => {
    setShowAssign(false);
    try {
      // Reassign is deliberately offered on already-claimed ideas, so the
      // precondition is "still claimed by whoever this card shows", not
      // "unclaimed" — that keeps the race closed without breaking Reassign.
      await EventSuggestionAPI.claim(suggestion.id, suggestion.pickedUpBy || null, targetVendorID);
      toast.success("Assigned to host");
    } catch (err) {
      console.error("[EventSuggestions] Assign error:", err);
      toast.error(err instanceof ClaimConflictError ? err.message : "Failed to assign");
    }
  };

  const handlePickUp = async () => {
    setPickingUp(true);
    try {
      await EventSuggestionAPI.claim(suggestion.id, null, vendorID);
      toast.success("Idea claimed!");
    } catch (err) {
      console.error("[EventSuggestions] Pick up error:", err);
      toast.error(err instanceof ClaimConflictError ? err.message : "Failed to claim idea");
    } finally {
      setPickingUp(false);
    }
  };

  const handleCreate = () => {
    // Opening the form is NOT proof an event was created, so we no longer flip the
    // suggestion to "approved" here. We thread the suggestion id through
    // (buildSuggestionConversionState) and let FormSection mark it "converted" once
    // the event is actually written. Until then it stays "pending", so an abandoned
    // conversion remains in "Finish creating" and can be retried.
    navigate("/events/new", { state: buildSuggestionConversionState(suggestion) });
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this idea?")) {
      return;
    }
    try {
      await EventSuggestionAPI.deleteSuggestion(suggestion.id);
      toast.success("Idea deleted");
    } catch (err) {
      console.error("[EventSuggestions] Delete error:", err);
      toast.error("Failed to delete idea");
    }
  };

  const confidenceBadge = getConfidenceBadge(suggestion.confidenceScore);
  const pct = suggestion.confidenceScore != null ? Math.round(suggestion.confidenceScore * 100) : null;
  const distance = origin && suggestion.coords?.lat != null
    ? formatMiles(getDistanceMiles(origin, suggestion.coords))
    : null;

  return (
    <div className={
      mapMode
        ? `flex flex-col ${isPickedUpByMe ? "ring-4 ring-primary ring-inset" : ""}`
        : `bg-white dark:bg-gray-800 rounded-lg shadow-md flex flex-col overflow-hidden ${isPickedUpByMe ? "border-[3px] border-primary ring-4 ring-primary/25" : "border border-gray-200 dark:border-gray-700"}`
    }>

      {/* Event image */}
      {suggestion.imageUrl && (
        <div
          className={`w-full aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0 relative${!mapMode ? " cursor-pointer" : ""}`}
          onClick={!mapMode ? () => setCardOpen((v) => !v) : undefined}
          onMouseEnter={handleImageEnter}
          onMouseLeave={handleImageLeave}
        >
          <img
            src={variantURL(suggestion.imageUrl, 800)}
            onError={fallbackToOriginal(suggestion.imageUrl)}
            alt={suggestion.eventName}
            className="w-full h-full object-cover"
          />
          {/* No z-index: it must stack above the still image but stay under the
              z-10 badges and the gradient scrim that follow it in source order. */}
          {hasVideo && (hasHovered || cardOpen) && (
            <video
              ref={videoRef}
              src={suggestion.videoUrl}
              muted
              loop
              playsInline
              preload="none"
              onPlaying={() => setVideoPlaying(true)}
              onError={() => { setVideoFailed(true); setVideoPlaying(false); }}
              className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300 ${videoPlaying ? "opacity-100" : "opacity-0"}`}
            />
          )}
          {hasVideo && !videoPlaying && (
            <span className="absolute bottom-2 right-2 z-10 inline-flex items-center rounded-full bg-black/55 p-1.5 text-white backdrop-blur-sm">
              <PlayCircleIcon className="h-4 w-4 shrink-0" />
            </span>
          )}
          {(isPickedUpByMe || (isPickedUpByOther && isSuperAdmin)) && (
            <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold shadow ${isPickedUpByMe ? "bg-primary text-white" : "bg-amber-500 text-white"}`}>
              <HandRaisedIcon className="h-3 w-3 shrink-0" />
              {isPickedUpByMe ? "You claimed this" : `Claimed by ${pickedUpByName}`}
            </div>
          )}
          {/* Projected earnings — subtle top-left badge (collapsed only) */}
          {!cardOpen && !mapMode && (() => {
            const proj = suggestion.revenueProjection || {};
            const min = proj.estimatedRevenueMin;
            const max = proj.estimatedRevenueMax;
            const fallback = suggestion.projectedRevenue ?? proj.estimatedRevenue;
            const fmt = (n) => `$${Math.round(n).toLocaleString("en-US")}`;
            let earn = null;
            if (min != null && max != null) earn = `${fmt(min)}–${fmt(max)}`;
            else if (fallback != null) earn = `${fmt(fallback)} est.`;
            if (!earn) return null;
            return (
              <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-emerald-600/90 backdrop-blur-sm px-2 py-0.5 text-xs font-semibold text-white shadow">
                <CurrencyDollarIcon className="h-3.5 w-3.5 shrink-0" />
                <span>{earn}</span>
              </div>
            );
          })()}
          {!cardOpen && !mapMode && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent flex flex-col justify-end p-3">
              <p className="text-white text-sm font-semibold leading-snug line-clamp-2 [text-shadow:0_1px_3px_rgba(0,0,0,0.8)]">{suggestion.eventName}</p>
              <div className={`flex items-center gap-2 mt-1 min-w-0 overflow-hidden [text-shadow:0_1px_2px_rgba(0,0,0,0.8)]${hasVideo ? " pr-8" : ""}`}>
                {suggestion.likeCount > 0 && (
                  <span className="flex items-center gap-1 text-white/90 text-xs shrink-0">
                    <HeartIcon className="h-3 w-3 shrink-0 text-rose-400" />
                    <span>{suggestion.likeCount}</span>
                  </span>
                )}
                {(suggestion.suggestedDate || suggestion.suggestedTime) && (
                  <span className="flex items-center gap-1 text-white/80 text-xs shrink-0">
                    <CalendarIcon className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {suggestion.suggestedDate && suggestion.suggestedDate.toDate().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                      {suggestion.suggestedTime && ` · ${suggestion.suggestedTime}`}
                    </span>
                  </span>
                )}
                {suggestion.venue && (
                  <span className="flex items-center gap-1 text-white/80 text-xs min-w-0">
                    <MapPinIcon className="h-3 w-3 shrink-0" />
                    <span className="truncate">{suggestion.venue}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {(cardOpen || !suggestion.imageUrl || mapMode) && <>
        {/* Picked up banner */}
        {isPickedUpByMe && (
          <div className="flex items-center gap-1.5 bg-primary/10 px-4 py-1.5 border-b border-primary/20 text-xs font-medium text-primary">
            <HandRaisedIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">You claimed this</span>
          </div>
        )}
        {isPickedUpByOther && isSuperAdmin && (
          <div className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/30 px-4 py-1.5 border-b border-amber-100 dark:border-amber-800 text-xs font-medium text-amber-700 dark:text-amber-300">
            <HandRaisedIcon className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">Claimed by {pickedUpByName}</span>
          </div>
        )}

        {/* Card Header */}
        <div className="p-4 pb-3 flex-1">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug flex-1">
              {suggestion.eventName}
            </h3>
            {pct != null && (
              <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${confidenceBadge.color}`}>
                {pct}%
              </span>
            )}
          </div>

          {/* Theme pill */}
          {suggestion.theme && (
            <span className="inline-block bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-xs font-medium mb-3">
              {suggestion.theme}
            </span>
          )}

          {/* Key details */}
          <div className="space-y-1.5 mb-3 text-xs text-gray-600 dark:text-gray-300">
            {suggestion.likeCount > 0 && (
              <div>
                {isSuperAdmin ? (
                  <button
                    type="button"
                    onClick={toggleLikers}
                    className="flex items-center gap-1.5 text-left hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                    title="See who liked this"
                  >
                    <HeartIcon className="h-3.5 w-3.5 text-rose-500 shrink-0" />
                    <span className="underline decoration-dotted underline-offset-2">
                      {suggestion.likeCount === 1
                        ? "1 member likes this"
                        : `${suggestion.likeCount} members like this`}
                    </span>
                    {likersLoading ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-rose-400" />
                    ) : (
                      <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 transition-transform ${showLikers ? "rotate-180" : ""}`} />
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <HeartIcon className="h-3.5 w-3.5 text-rose-500 shrink-0" />
                    <span>
                      {suggestion.likeCount === 1
                        ? "1 member likes this"
                        : `${suggestion.likeCount} members like this`}
                    </span>
                  </div>
                )}

                {isSuperAdmin && showLikers && likers !== null && (
                  <ul className="mt-2 space-y-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 p-2">
                    {likers.length === 0 ? (
                      <li className="text-gray-400 dark:text-gray-500 italic px-1">No likers found</li>
                    ) : (
                      likers.map((liker) => {
                        const fromCache = usersById[liker.uid];
                        const name = liker.name || fromCache?.name || fromCache?.email || fromCache?.phone || `member …${String(liker.uid).slice(-4)}`;
                        const photoURL = liker.photoURL || fromCache?.photoURL || null;
                        return (
                          <li key={liker.uid} className="flex items-center gap-2">
                            {photoURL ? (
                              <img
                                src={variantURL(photoURL, 200)}
                                onError={fallbackToOriginal(photoURL)}
                                alt=""
                                className="h-6 w-6 rounded-full object-cover shrink-0 bg-gray-200 dark:bg-gray-700"
                              />
                            ) : (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase shrink-0">
                                {String(name).trim().charAt(0) || "?"}
                              </span>
                            )}
                            <span className="truncate text-gray-700 dark:text-gray-200">{name}</span>
                          </li>
                        );
                      })
                    )}
                  </ul>
                )}
              </div>
            )}
            {(suggestion.suggestedDate || suggestion.suggestedTime) && (
              <div className="flex items-center gap-1.5">
                <CalendarIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                <span>
                  {suggestion.suggestedDate && (
                    <span className="text-gray-700 dark:text-gray-200 font-medium">
                      {suggestion.suggestedDate.toDate().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                    </span>
                  )}
                  {suggestion.suggestedTime && (
                    <span className="text-gray-500 dark:text-gray-400"> · {suggestion.suggestedTime}</span>
                  )}
                  {suggestion.suggestedDate && (
                    <span className={`ml-1.5 text-xs font-medium ${getRelativeDateColor(suggestion.suggestedDate)}`}>
                      ({getRelativeDate(suggestion.suggestedDate)})
                    </span>
                  )}
                </span>
              </div>
            )}
            {(suggestion.venue || distance) && (
              <div className="flex items-center gap-1.5">
                <MapPinIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                {suggestion.venue && <span className="truncate">{suggestion.venue}</span>}
                {distance && (
                  <span className="shrink-0 whitespace-nowrap font-medium text-primary">
                    · {distance} away
                  </span>
                )}
              </div>
            )}
            {suggestion.targetAudience && (
              <div className="flex items-center gap-1.5">
                <UserGroupIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                <span className="truncate">{suggestion.targetAudience}</span>
              </div>
            )}
            {(suggestion.estimatedCost || suggestion.ageRange) && (
              <div className="flex items-center gap-3">
                {suggestion.estimatedCost && (
                  <div className="flex items-center gap-1">
                    <CurrencyDollarIcon className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 shrink-0" />
                    <span>{suggestion.estimatedCost}</span>
                  </div>
                )}
                {suggestion.ageRange && (
                  <span className="text-gray-400 dark:text-gray-500">Ages {suggestion.ageRange}</span>
                )}
              </div>
            )}
          </div>

          {/* Location map — where the event is. Hidden by default in mapMode
              (the split-map side panel already sits next to the full map), but
              callers without an adjacent map (e.g. the preview modal) can opt
              back in via showMap. */}
          {showMap && suggestion.coords?.lat != null && (
            <MiniMap coords={suggestion.coords} label={suggestion.venue || suggestion.eventName} />
          )}

          {/* Projected earnings (location-aware) */}
          {(() => {
            const proj = suggestion.revenueProjection || {};
            const min = proj.estimatedRevenueMin;
            const max = proj.estimatedRevenueMax;
            const fallback = suggestion.projectedRevenue ?? proj.estimatedRevenue;
            const fmt = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

            let earnText = null;
            if (min != null && max != null) earnText = `Earn ${fmt(min)}–${fmt(max)}`;
            else if (fallback != null) earnText = `${fmt(fallback)} est.`;
            if (!earnText) return null;

            const attendance = suggestion.estimatedAttendance;
            const areaLabel = proj.location?.areaLabel;

            const mom = proj.momentum;
            const momTrend = mom && mom.source === "events" && (mom.trend === "rising" || mom.trend === "cooling")
              ? mom.trend
              : null;

            return (
              <div className="mb-3 rounded-lg border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/20 px-2.5 py-2 space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600/70 dark:text-emerald-400/70">
                  Projected host earnings
                </div>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  <CurrencyDollarIcon className="h-4 w-4 shrink-0" />
                  <span>{earnText}</span>
                </div>
                {attendance != null && (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700/80 dark:text-emerald-300/80">
                    <ChartBarIcon className="h-3.5 w-3.5 shrink-0" />
                    <span>~{attendance} expected {attendance === 1 ? "guest" : "guests"}</span>
                  </div>
                )}
                {areaLabel && (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-700/80 dark:text-emerald-300/80">
                    <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{areaLabel}</span>
                  </div>
                )}
                {momTrend && (() => {
                  const rising = momTrend === "rising";
                  const Icon = rising ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
                  return (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${rising
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-800/50 dark:text-emerald-200"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}
                      title={`Platform momentum: recent events are ${rising ? "outperforming" : "underperforming"} the prior period`}
                    >
                      <Icon className="h-3 w-3 shrink-0" />
                      <span>Momentum {rising ? "rising" : "cooling"}</span>
                    </span>
                  );
                })()}
              </div>
            );
          })()}

          {/* Description */}
          {suggestion.description && (
            <div className="mb-3">
              <p className={`text-sm text-gray-600 dark:text-gray-300 leading-relaxed ${!expanded ? (mapMode ? "line-clamp-2" : "line-clamp-3") : ""}`}>
                {suggestion.description}
              </p>
              {suggestion.description.length > 150 && (
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="mt-1 text-xs text-primary hover:text-primaryDark font-medium"
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}

          {/* Reasoning */}
          {suggestion.reasoning && (
            <div className="border-t border-gray-100 dark:border-gray-800 pt-2 mb-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Why this event?</p>
              <p className={`text-xs text-gray-500 dark:text-gray-400 leading-relaxed ${!showReasoning ? (mapMode ? "line-clamp-2" : "line-clamp-3") : ""}`}>
                {suggestion.reasoning}
              </p>
              {suggestion.reasoning.length > 150 && (
                <button
                  onClick={() => setShowReasoning(!showReasoning)}
                  className="mt-1 text-xs text-primary hover:text-primaryDark font-medium"
                >
                  {showReasoning ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}

          {/* Inspired by + source links */}
          {(suggestion.trendingEventReference || (suggestion.sourceUrls && suggestion.sourceUrls.length > 0)) && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              {suggestion.trendingEventReference && (
                <p className="mb-1 italic">Inspired by: {suggestion.trendingEventReference}</p>
              )}
              {suggestion.sourceUrls && suggestion.sourceUrls.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {suggestion.sourceUrls.slice(0, 2).map((url, i) => (
                    <a
                      key={i}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-0.5 text-primary hover:underline"
                    >
                      Source {i + 1}
                      <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Similar past events (vector KNN over real history) */}
          <SimilarEventsPanel suggestion={suggestion} />
        </div>

        {/* Action Buttons */}
        <SuggestionCardActions
          mapMode={mapMode}
          suggestion={suggestion}
          isPickedUpByMe={isPickedUpByMe}
          isPickedUpByOther={isPickedUpByOther}
          isSuperAdmin={isSuperAdmin}
          handlePickUp={handlePickUp}
          pickingUp={pickingUp}
          onUpdateStatus={onUpdateStatus}
          handleCreate={handleCreate}
          moreMenuRef={moreMenuRef}
          showMoreMenu={showMoreMenu}
          setShowMoreMenu={setShowMoreMenu}
          handleReturnToPool={handleReturnToPool}
          showAssign={showAssign}
          setShowAssign={setShowAssign}
          handleAssign={handleAssign}
          hosts={hosts}
          handleDelete={handleDelete}
        />
      </>}
    </div>
  );
};
